import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ContestBudgetClassification } from '@freelancer/datastore/collections';
import { FontWeight, TextSize, TextTransform } from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

// Values based on /project-create/contest/contest-budget-selector/flContestBudgetSelector.js
enum ContestBudgetExpectedEntries {
  OK = 30,
  GOOD = 80,
  GREAT = 100,
  EXCELLENT = 150,
  BEST = 250,
}

@Component({
  selector: 'app-contest-expected-entries',
  template: `
    <fl-bit
      class="BudgetFeedback"
      *ngIf="budgetClassification$ | async as budgetClassification"
      [attr.data-color]="budgetClassification"
      [ngSwitch]="budgetClassification"
    >
      <fl-text
        [size]="TextSize.SMALL"
        [textTransform]="TextTransform.UPPERCASE"
        [weight]="FontWeight.BOLD"
      >
        <ng-container
          *ngSwitchCase="ContestBudgetClassification.OK"
          i18n="Post Job Page form expected results for contest budget"
        >
          OK Results
        </ng-container>
        <ng-container
          *ngSwitchCase="ContestBudgetClassification.GOOD"
          i18n="Post Job Page form expected results for contest budget"
        >
          Good Results
        </ng-container>
        <ng-container
          *ngSwitchCase="ContestBudgetClassification.GREAT"
          i18n="Post Job Page form expected results for contest budget"
        >
          Great Results
        </ng-container>
        <ng-container
          *ngSwitchCase="ContestBudgetClassification.EXCELLENT"
          i18n="Post Job Page form expected results for contest budget"
        >
          Excellent Results
        </ng-container>
        <ng-container
          *ngSwitchCase="ContestBudgetClassification.BEST"
          i18n="Post Job Page form expected results for contest budget"
        >
          Best Results
        </ng-container>
        <ng-container
          *ngSwitchDefault
          i18n="Post Job Page form expected results for contest budget"
        >
          Amazing Results
        </ng-container>
      </fl-text>
      <fl-text>
        <ng-container
          *ngIf="
            budgetClassification !== ContestBudgetClassification.AMAZING;
            else amazingResults
          "
          i18n="Post Job Page expected number of entries for contest budget"
        >
          Expect around {{ expectedEntries$ | async }} entries
        </ng-container>
        <ng-template #amazingResults>
          <ng-container
            i18n="Post Job Page expected number of entries for contest budget"
          >
            Expect the unexpected!
          </ng-container>
        </ng-template>
      </fl-text>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./expected-entries.component.scss'],
})
export class ExpectedEntriesComponent implements OnInit {
  ContestBudgetClassification = ContestBudgetClassification;
  FontWeight = FontWeight;
  TextSize = TextSize;
  TextTransform = TextTransform;

  @Input() budgetClassification$: Rx.Observable<ContestBudgetClassification>;

  expectedEntries$: Rx.Observable<number | undefined>;

  ngOnInit() {
    this.expectedEntries$ = this.budgetClassification$.pipe(
      filter(isDefined),
      map(classification => this.getExpectedNumberOfEntries(classification)),
      distinctUntilChanged(),
    );
  }

  getExpectedNumberOfEntries(
    classification: ContestBudgetClassification,
  ): number | undefined {
    switch (classification) {
      case ContestBudgetClassification.OK:
        return ContestBudgetExpectedEntries.OK;
      case ContestBudgetClassification.GOOD:
        return ContestBudgetExpectedEntries.GOOD;
      case ContestBudgetClassification.GREAT:
        return ContestBudgetExpectedEntries.GREAT;
      case ContestBudgetClassification.EXCELLENT:
        return ContestBudgetExpectedEntries.EXCELLENT;
      case ContestBudgetClassification.BEST:
        return ContestBudgetExpectedEntries.BEST;
      case ContestBudgetClassification.AMAZING:
      default:
        return undefined;
    }
  }
}
